import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { asDecimalString } from "@shop-ledger/shared-types";
import {
  Button,
  DataTable,
  Input,
  Modal,
  StockIndicator,
  type DataTableColumn,
  usePagination,
  useSortableData,
} from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createInventoryItem, listInventory } from "../../api/inventoryApi";
import type { InventoryItemOutExt } from "../../api/types";
import { canEnableSerializedTracking, canWriteInventory, canWritePurchaseOrders } from "../../roles/permissions";
import { ApiError } from "../../lib/apiClient";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { BranchSelect } from "../../components/BranchSelect";
import { CategorySelect } from "../../components/CategorySelect";
import { CustomFieldsEditor } from "../../components/CustomFieldsEditor";
import { SupplierSelect } from "../../components/SupplierSelect";
import { TaxRateSelect } from "../../components/TaxRateSelect";
import { TopUpStockModal } from "../../components/TopUpStockModal";
import { BarcodeScanButton } from "../../components/BarcodeScanButton";
import { ScanToAddStockModal } from "../../components/ScanToAddStockModal";
import { UnitOfMeasureSelect } from "../../components/UnitOfMeasureSelect";
import { generateSku } from "../../lib/generateSku";

type InventoryItemOut = InventoryItemOutExt;

export function InventoryListPage() {
  const { user, ensureFreshToken } = useAuth();
  const [branchId, setBranchId] = useState(user?.assigned_branch_id ?? "");
  useEffect(() => {
    if (user?.assigned_branch_id) setBranchId(user.assigned_branch_id);
  }, [user?.assigned_branch_id]);

  const itemsState = useAsyncData(
    () => ensureFreshToken().then((token) => listInventory(token, branchId || undefined)),
    [branchId],
  );

  const existingCategories = useMemo(() => {
    const set = new Set<string>();
    for (const item of itemsState.data ?? []) {
      if (item.category) set.add(item.category);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [itemsState.data]);

  const existingUnits = useMemo(() => {
    const set = new Set<string>();
    for (const item of itemsState.data ?? []) {
      if (item.unit_of_measure) set.add(item.unit_of_measure);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [itemsState.data]);

  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  const filteredData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return (itemsState.data ?? []).filter((item) => {
      const matchesQuery =
        query.length === 0 || item.name.toLowerCase().includes(query) || item.sku.toLowerCase().includes(query);
      const matchesCategory = categoryFilter === "" || item.category === categoryFilter;
      return matchesQuery && matchesCategory;
    });
  }, [itemsState.data, searchQuery, categoryFilter]);

  const { sortedData, sort, requestSort } = useSortableData<InventoryItemOut, keyof InventoryItemOut>(
    filteredData,
    (row, key) => String(row[key]),
  );
  const { pageData, pagination, setPage } = usePagination(sortedData, 25);

  const canWrite = user ? canWriteInventory(user.role) : false;
  const canTopUp = user ? canWritePurchaseOrders(user.role) : false;
  const canEnableSerialized = user ? canEnableSerializedTracking(user.role) : false;
  const [topUpItem, setTopUpItem] = useState<InventoryItemOut | null>(null);
  const [scanToStockOpen, setScanToStockOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [retailPrice, setRetailPrice] = useState("");
  const [wholesalePrice, setWholesalePrice] = useState("");
  const [lowStockThreshold, setLowStockThreshold] = useState("5");
  const [createBranchId, setCreateBranchId] = useState(user?.assigned_branch_id ?? "");
  const [category, setCategory] = useState("");
  const [unitOfMeasure, setUnitOfMeasure] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [taxRateId, setTaxRateId] = useState("");
  const [isSerialized, setIsSerialized] = useState(false);
  const [isEcommerceVisible, setIsEcommerceVisible] = useState(true);
  const [customFields, setCustomFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      await createInventoryItem(token, {
        branch_id: createBranchId,
        sku,
        name,
        retail_price: asDecimalString(retailPrice),
        wholesale_price: wholesalePrice ? asDecimalString(wholesalePrice) : undefined,
        low_stock_threshold: asDecimalString(lowStockThreshold),
        category: category || undefined,
        unit_of_measure: unitOfMeasure || undefined,
        supplier_id: supplierId || undefined,
        batch_number: batchNumber || undefined,
        expiry_date: expiryDate || undefined,
        tax_rate_id: taxRateId || undefined,
        is_serialized: isSerialized,
        is_ecommerce_visible: isEcommerceVisible,
        custom_fields: customFields,
      });
      setSku("");
      setName("");
      setRetailPrice("");
      setWholesalePrice("");
      setCategory("");
      setUnitOfMeasure("");
      setSupplierId("");
      setBatchNumber("");
      setExpiryDate("");
      setTaxRateId("");
      setIsSerialized(false);
      setIsEcommerceVisible(true);
      setCustomFields({});
      setCreateModalOpen(false);
      itemsState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create item.");
    } finally {
      setSubmitting(false);
    }
  }

  const columns: DataTableColumn<InventoryItemOut, keyof InventoryItemOut>[] = [
    {
      key: "sku",
      header: "SKU",
      sortable: true,
      render: (r) => (
        <span className="sl-cell-truncate" title={r.sku}>
          {r.sku}
        </span>
      ),
    },
    {
      key: "name",
      header: "Name",
      sortable: true,
      render: (r) => (
        <Link to={`/stock/${r.id}`}>{r.name}</Link>
      ),
    },
    { key: "category", header: "Category", sortable: true, render: (r) => r.category ?? "—" },
    { key: "retail_price", header: "Retail Price", sortable: true, align: "right", render: (r) => r.retail_price },
    {
      key: "current_stock",
      header: "Stock",
      sortable: true,
      align: "right",
      render: (r) => (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <StockIndicator currentStock={r.current_stock} lowStockThreshold={r.low_stock_threshold} showLabel />
          {r.unit_of_measure ? <span className="muted">{r.unit_of_measure}</span> : null}
        </span>
      ),
    },
    { key: "low_stock_threshold", header: "Low Threshold", align: "right", render: (r) => r.low_stock_threshold },
    {
      key: "nearest_expiry_date",
      header: "Expiry",
      render: (r) => r.nearest_expiry_date ?? "—",
    },
    {
      key: "is_ecommerce_visible",
      header: "Online",
      render: (r) => (
        <span className={`badge ${r.is_ecommerce_visible ? "badge--current" : "badge--overdue"}`}>
          {r.is_ecommerce_visible ? "Yes" : "No"}
        </span>
      ),
    },
    ...(canTopUp
      ? ([
          {
            key: "id",
            header: "",
            render: (r) => (
              <button type="button" className="sl-button sl-button--ghost sl-button--md" onClick={() => setTopUpItem(r)}>
                Top up stock
              </button>
            ),
          },
        ] as DataTableColumn<InventoryItemOut, keyof InventoryItemOut>[])
      : []),
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Inventory</h1>
          <p className="page__subtitle">Stock levels, pricing, category/supplier/batch depth, and the audit trail per item.</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {canWrite ? (
            <Button type="button" onClick={() => setCreateModalOpen(true)}>
              Add new product
            </Button>
          ) : null}
          {canTopUp ? (
            <Button type="button" variant="secondary" onClick={() => setScanToStockOpen(true)}>
              Scan to add stock
            </Button>
          ) : null}
          <Link to="/stock?tab=import-export" className="sl-button sl-button--secondary sl-button--md">
            Bulk import / export
          </Link>
        </div>
      </div>

      {itemsState.error ? <StatusBanner kind="error">{itemsState.error}</StatusBanner> : null}

      {!canWrite && user ? (
        <StatusBanner kind="info">
          Your role ({user.role}) has read access to inventory. Creating/editing items is restricted to
          OWNER/BRANCH_MANAGER/ECOM_MGR by the backend (<code>require_roles</code> on <code>POST/PATCH /inventory</code>).
        </StatusBanner>
      ) : null}

      <div className="panel">
        <div className="filter-bar">
          <div className="sl-field">
            <label className="sl-field__label" htmlFor="inv-search">
              Search
            </label>
            <input
              id="inv-search"
              type="search"
              className="sl-input"
              placeholder="Search by name or SKU…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <BranchSelect id="inv-branch-filter" label="Branch" value={branchId} onChange={setBranchId} allOptionLabel="All branches" />
          <div className="sl-field">
            <label className="sl-field__label" htmlFor="inv-category-filter">
              Category
            </label>
            <select
              id="inv-category-filter"
              className="sl-input"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All categories</option>
              {existingCategories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
        </div>
        {itemsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={pageData}
            getRowId={(row) => row.id}
            sort={sort}
            onSortChange={requestSort}
            pagination={pagination}
            onPageChange={setPage}
            emptyMessage="No inventory items found."
            caption="Inventory"
          />
        )}
      </div>

      {canWrite ? (
        <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)} title="Add new product">
          <form onSubmit={handleCreate}>
            <div className="form-grid">
              <div className="sl-field-with-action">
                <Input
                  label="SKU"
                  value={sku}
                  onChange={(e) => setSku(e.target.value)}
                  placeholder="Type, scan, or generate one"
                  required
                />
                <BarcodeScanButton onScan={setSku} label="Scan barcode" />
                <Button type="button" variant="secondary" onClick={() => setSku(generateSku())}>
                  Generate
                </Button>
              </div>
              <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
              <Input
                label="Retail price"
                inputMode="decimal"
                value={retailPrice}
                onChange={(e) => setRetailPrice(e.target.value)}
                placeholder="0.00"
                required
              />
              <Input
                label="Wholesale price"
                inputMode="decimal"
                value={wholesalePrice}
                onChange={(e) => setWholesalePrice(e.target.value)}
                placeholder="0.00"
                hint="What you pay to stock this item — optional, used for margin reporting."
              />
              <Input
                label="Low stock threshold"
                inputMode="decimal"
                value={lowStockThreshold}
                onChange={(e) => setLowStockThreshold(e.target.value)}
              />
              <BranchSelect id="inv-create-branch" label="Branch" value={createBranchId} onChange={setCreateBranchId} required />
              <CategorySelect id="inv-create-category" label="Category" value={category} onChange={setCategory} existingCategories={existingCategories} />
              <UnitOfMeasureSelect id="inv-create-uom" label="Unit of measure" value={unitOfMeasure} onChange={setUnitOfMeasure} existingUnits={existingUnits} />
              <SupplierSelect id="inv-create-supplier" label="Supplier" value={supplierId} onChange={setSupplierId} />
              <Input label="Batch number" value={batchNumber} onChange={(e) => setBatchNumber(e.target.value)} />
              <Input label="Expiry date" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
              <TaxRateSelect id="inv-create-tax-rate" label="Tax rate" value={taxRateId} onChange={setTaxRateId} />
              {canEnableSerialized ? (
                <label className="sl-field">
                  <span className="sl-field__label">Track serial numbers</span>
                  <input type="checkbox" checked={isSerialized} onChange={(e) => setIsSerialized(e.target.checked)} />
                  <span className="sl-field__hint">
                    Turn this on to record each unit's own serial number (e.g. electronics, appliances) instead of
                    just a quantity count — useful for warranty claims and returns. Most products should leave this
                    off. Only the owner can turn this on.
                  </span>
                </label>
              ) : null}
              <label className="sl-field">
                <span className="sl-field__label">Available online</span>
                <input
                  type="checkbox"
                  checked={isEcommerceVisible}
                  onChange={(e) => setIsEcommerceVisible(e.target.checked)}
                />
              </label>
            </div>
            <CustomFieldsEditor value={customFields} onChange={setCustomFields} />
            <p className="muted" style={{ marginTop: 8 }}>
              Add photos after creating the item — open it from the list below.
            </p>
            {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
            <div className="form-actions">
              <Button type="submit" disabled={submitting}>
                {submitting ? "Creating…" : "Create item"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}

      {topUpItem ? (
        <TopUpStockModal
          open
          onClose={() => setTopUpItem(null)}
          itemId={topUpItem.id}
          itemName={topUpItem.name}
          branchId={topUpItem.branch_id}
          onToppedUp={() => itemsState.reload()}
        />
      ) : null}

      {canTopUp ? (
        <ScanToAddStockModal
          open={scanToStockOpen}
          onClose={() => setScanToStockOpen(false)}
          onToppedUp={() => itemsState.reload()}
        />
      ) : null}
    </div>
  );
}
